/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { useCallback, useEffect, useState } from "react";

declare global {
  interface Document {
    mozCancelFullScreen?: () => Promise<void>;
    msExitFullscreen?: () => Promise<void>;
    webkitExitFullscreen?: () => Promise<void>;
    mozFullScreenElement?: Element;
    msFullscreenElement?: Element;
    webkitFullscreenElement?: Element;
    mozFullScreen?: boolean;
    webkitIsFullScreen?: boolean;
  }

  interface HTMLElement {
    msRequestFullscreen?: () => Promise<void>;
    mozRequestFullscreen?: () => Promise<void>;
    webkitRequestFullscreen?: () => Promise<void>;
  }
}

interface useToggleFullScreenValue {
  toggle: () => Promise<void>;
  fullScreen: boolean;
}

const isOnFullScreen = () =>
  Boolean(
    document.fullscreenElement ||
      document.mozFullScreen ||
      document.webkitIsFullScreen ||
      document.msFullscreenElement,
  );

export const useToggleFullScreen = (
  element: HTMLElement | null,
): useToggleFullScreenValue => {
  const [fullScreen, setFullScreen] = useState<boolean>(isOnFullScreen());

  const toggle = useCallback((): Promise<void> => {
    const exitFullScreen =
      document.exitFullscreen ||
      document.webkitExitFullscreen ||
      document.mozCancelFullScreen ||
      document.msExitFullscreen;

    const requestFullScreen =
      element?.requestFullscreen ||
      element?.webkitRequestFullscreen ||
      element?.mozRequestFullscreen ||
      element?.msRequestFullscreen;

    if (isOnFullScreen()) {
      return exitFullScreen.call(document);
    }

    if (requestFullScreen) {
      return requestFullScreen.call(element).catch(() => {
        console.warn("Unable to enter full screen");
      });
    }

    return Promise.resolve();
  }, [element]);

  useEffect(() => {
    const handler = () => setFullScreen(isOnFullScreen());

    element?.addEventListener("fullscreenchange", handler);

    return () => element?.removeEventListener("fullscreenchange", handler);
  }, [element]);

  return {
    toggle,
    fullScreen,
  };
};
